import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import type { HomeReview, ReviewState } from '../../types';
import { logout } from './authSlice';
import {
  extractOnDemandItems,
  mergeResponseWithTimestamp,
  mergeReviewData,
  normalizeItemId,
  updateItemInCategories,
} from './reviewSlice.helpers';

type ReviewAsyncThunk = any;

type ReviewAsyncThunks = {
  fetchReviews: ReviewAsyncThunk;
  fetchReviewById: ReviewAsyncThunk;
  fetchTemplates: ReviewAsyncThunk;
  fetchTemplateDetails: ReviewAsyncThunk;
  createReview: ReviewAsyncThunk;
  updateReviewResponse: ReviewAsyncThunk;
  updateSubmittedReviewResponse: ReviewAsyncThunk;
  submitReview: ReviewAsyncThunk;
  deleteReview: ReviewAsyncThunk;
  completeReview: ReviewAsyncThunk;
  fetchOnDemandItems: ReviewAsyncThunk;
  createOnDemandItem: ReviewAsyncThunk;
  updateOnDemandItem: ReviewAsyncThunk;
  deleteOnDemandItem: ReviewAsyncThunk;
  updateReviewNotes: ReviewAsyncThunk;
  updateReviewOnDemandProjects: ReviewAsyncThunk;
  updateReviewDetails: ReviewAsyncThunk;
  initializeOfflineServices: ReviewAsyncThunk;
  syncOfflineData: ReviewAsyncThunk;
  saveAllDrafts: ReviewAsyncThunk;
};

export const addReviewExtraReducers = (
  builder: ActionReducerMapBuilder<ReviewState>,
  thunks: ReviewAsyncThunks
) => {
  const {
    fetchReviews,
    fetchReviewById,
    fetchTemplates,
    fetchTemplateDetails,
    createReview,
    updateReviewResponse,
    updateSubmittedReviewResponse,
    submitReview,
    deleteReview,
    completeReview,
    fetchOnDemandItems,
    createOnDemandItem,
    updateOnDemandItem,
    deleteOnDemandItem,
    updateReviewNotes,
    updateReviewOnDemandProjects,
    updateReviewDetails,
    initializeOfflineServices,
    syncOfflineData,
    saveAllDrafts,
  } = thunks;

    builder
      // Fetch Reviews
      .addCase(fetchReviews.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchReviews.fulfilled, (state, action) => {
        state.isLoading = false;
        
        // Intelligently merge reviews to preserve template categories
        if (!Array.isArray(action.payload)) {
          state.reviews = [];
          return;
        }
        
        // For each new review, check if we already have it with more detailed data
        const mergedReviews = action.payload.map((newReview: HomeReview) => {
          const existingReview = state.reviews.find(r => r.id === newReview.id);
          
          if (existingReview) {
            // Check if existing has template categories but new one doesn't
            const existingHasCategories = (existingReview.template?.categories?.length || 0) > 0;
            const newHasCategories = (newReview.template?.categories?.length || 0) > 0;
            
            if (existingHasCategories && !newHasCategories) {
              // Preserve existing review with categories, but update other fields
              return {
                ...existingReview,
                status: newReview.status,
                updatedAt: newReview.updatedAt,
                property: newReview.property || existingReview.property,
                // Keep existing template with categories
                template: existingReview.template
              };
            }
          }
          
          // Use new review data (either no existing review or new has categories)
          return newReview;
        });
        
        // Use the merged reviews data directly (don't preserve missing reviews - they may be soft deleted)
        state.reviews = mergedReviews;
      })
      .addCase(fetchReviews.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch reviews';
      })
      // Fetch Review by ID
      .addCase(fetchReviewById.pending, (state) => {
        // Only show loading if we don't already have cached data
        // This allows instant rendering with cache while refresh runs in background
        state.isLoadingReview = true;
      })
      .addCase(fetchReviewById.fulfilled, (state, action) => {
        state.isLoadingReview = false;
        state.error = null; // Clear any previous errors

        // Ensure reviewData exists for web components (safety net)
        if (!(action.payload as any).reviewData) {
          (action.payload as any).reviewData = {
            template: action.payload.template,
            responses: action.payload.responses || [],
            status: action.payload.status,
            submittedAt: action.payload.submittedAt,
            completedAt: action.payload.completedAt,
            property: action.payload.property,
          };
        }

        // Update or add review to reviews array using smart merge
        const existingIndex = state.reviews.findIndex(r => r.id === action.payload.id);
        if (existingIndex !== -1) {
          const existingReview = state.reviews[existingIndex];
          // Use merge helper to intelligently combine local and API data
          const mergedReview = mergeReviewData(existingReview, action.payload);

          // Safety net: preserve existing template if merged result lacks categories
          // This protects against API returning a template shell (id/name only) without categories
          const existingHasCategories = (existingReview.template?.categories?.length || 0) > 0;
          const mergedHasCategories = (mergedReview.template?.categories?.length || 0) > 0;

          if (existingHasCategories && !mergedHasCategories) {
            mergedReview.template = existingReview.template;
            // Also update reviewData.template for web compatibility
            if ((mergedReview as any).reviewData) {
              (mergedReview as any).reviewData.template = existingReview.template;
            }
          }

          state.reviews[existingIndex] = mergedReview;
          // Set as activeReview for consistency with other flows
          state.activeReview = mergedReview;
        } else {
          state.reviews.push(action.payload);
          // Set as activeReview for newly added reviews
          state.activeReview = action.payload;
        }

        // Extract and store onDemandItems if present
        if (action.payload.onDemandItems) {
          if (!state.onDemandItems) {
            state.onDemandItems = {};
          }
          state.onDemandItems[action.payload.id] = action.payload.onDemandItems;
        } else {
          // When offline, on-demand items are embedded in the template categories
          // Extract them recursively (handles nested subcategories)
          const onDemandItemsList = extractOnDemandItems(action.payload.template?.categories);

          if (onDemandItemsList.length > 0) {
            if (!state.onDemandItems) {
              state.onDemandItems = {};
            }
            state.onDemandItems[action.payload.id] = onDemandItemsList;
          }
        }

        // Removed duplicate rooms refresh - already done in reviewService.getReviewDetails()
      })
      .addCase(fetchReviewById.rejected, (state, action) => {
        state.isLoadingReview = false;
        const reviewId = action.meta?.arg?.reviewId;
        const hasExistingReview = reviewId !== undefined
          ? state.reviews.some(r => Number(r.id) === Number(reviewId))
          : false;
        const message = action.error.message || 'Failed to fetch review';
        const isOfflineRefreshFailure =
          message.includes('offline') ||
          message.includes('not available from the API while offline');

        if (hasExistingReview && isOfflineRefreshFailure) {
          state.error = null;
          return;
        }

        state.error = action.error.message || 'Failed to fetch review';
      })
      // Fetch Templates
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.templates = action.payload;
      })
      // Fetch Template Details
      .addCase(fetchTemplateDetails.fulfilled, (state, action) => {
        // Update or add template to templates array
        const existingIndex = state.templates.findIndex(t => t.id === action.payload.id);
        if (existingIndex !== -1) {
          state.templates[existingIndex] = action.payload;
        } else {
          state.templates.push(action.payload);
        }
      })
      // Create Review
      .addCase(createReview.fulfilled, (state, action) => {
        state.activeReview = action.payload;
        state.reviews.push(action.payload);
      })
      // Update Response
      .addCase(updateReviewResponse.pending, (state) => {
      })
      .addCase(updateReviewResponse.fulfilled, (state, action) => {

        const reviewId = action.meta.arg.reviewId;
        const itemId = action.meta.arg.itemId;

        // Only default to 'synced' if payload doesn't have syncStatus
        // (offline edits return syncStatus: 'pending', must preserve it)
        const responseWithSyncStatus = {
          ...action.payload,
          syncStatus: action.payload.syncStatus ?? 'synced' as const
        };

        // Update activeReview if it matches
        if (state.activeReview && state.activeReview.id === reviewId) {
          // Initialize responses array if needed
          if (!state.activeReview.responses) {
            state.activeReview.responses = [];
          }

          // Find and merge or add response
          const index = state.activeReview.responses.findIndex(r => r.itemId === itemId);
          if (index !== -1) {
            const existingResponse = state.activeReview.responses[index];
            state.activeReview.responses[index] = mergeResponseWithTimestamp(existingResponse, responseWithSyncStatus);
          } else {
            state.activeReview.responses.push(responseWithSyncStatus);
          }
        }

        // Also update in reviews array
        const reviewIndex = state.reviews.findIndex(r => r.id === reviewId);
        if (reviewIndex !== -1) {
          // Initialize responses array if needed
          if (!state.reviews[reviewIndex].responses) {
            state.reviews[reviewIndex].responses = [];
          }

          const respIndex = state.reviews[reviewIndex].responses!.findIndex(r => r.itemId === itemId);
          if (respIndex !== -1) {
            const existingResponse = state.reviews[reviewIndex].responses![respIndex];
            state.reviews[reviewIndex].responses![respIndex] = mergeResponseWithTimestamp(existingResponse, responseWithSyncStatus);
          } else {
            state.reviews[reviewIndex].responses!.push(responseWithSyncStatus);
          }
        }
      })
      .addCase(updateReviewResponse.rejected, (state) => {
      })
      // Update Submitted Response
      .addCase(updateSubmittedReviewResponse.pending, (state) => {
      })
      .addCase(updateSubmittedReviewResponse.fulfilled, (state, action) => {

        // Only default to 'synced' if payload doesn't have syncStatus
        const responseWithSyncStatus = {
          ...action.payload,
          syncStatus: action.payload.syncStatus ?? 'synced' as const
        };

        // Update the response in all reviews (not just activeReview)
        const reviewIndex = state.reviews.findIndex(r => r.responses?.some(res => res.itemId === action.payload.itemId));
        if (reviewIndex !== -1 && state.reviews[reviewIndex].responses) {
          const responseIndex = state.reviews[reviewIndex].responses!.findIndex(r => r.itemId === action.payload.itemId);
          if (responseIndex !== -1) {
            const existingResponse = state.reviews[reviewIndex].responses![responseIndex];
            state.reviews[reviewIndex].responses![responseIndex] = mergeResponseWithTimestamp(existingResponse, responseWithSyncStatus);
          }
        }
        // Also update activeReview if it matches
        if (state.activeReview && state.activeReview.responses) {
          const index = state.activeReview.responses.findIndex(r => r.itemId === action.payload.itemId);
          if (index !== -1) {
            const existingResponse = state.activeReview.responses[index];
            state.activeReview.responses[index] = mergeResponseWithTimestamp(existingResponse, responseWithSyncStatus);
          }
        }
      })
      .addCase(updateSubmittedReviewResponse.rejected, (state, action) => {
      })
      // Submit Review
      .addCase(submitReview.pending, (state) => {
      })
      .addCase(submitReview.fulfilled, (state, action) => {
        const submittedReview = action.payload.review;
        
        // Update activeReview if it matches
        if (state.activeReview && state.activeReview.id === submittedReview.id) {
          state.activeReview.status = submittedReview.status;
          state.activeReview.submittedAt = submittedReview.submittedAt;
          if (submittedReview.vendorNotes) {
            state.activeReview.vendorNotes = submittedReview.vendorNotes;
          }
        }
        
        // Update the review in the reviews array
        const reviewIndex = state.reviews.findIndex(r => r.id === submittedReview.id);
        if (reviewIndex !== -1) {
          state.reviews[reviewIndex].status = submittedReview.status;
          state.reviews[reviewIndex].submittedAt = submittedReview.submittedAt;
          if (submittedReview.vendorNotes) {
            state.reviews[reviewIndex].vendorNotes = submittedReview.vendorNotes;
          }
        }
      })
      .addCase(submitReview.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to submit review';
      })
      // Delete Review
      .addCase(deleteReview.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteReview.fulfilled, (state, action) => {
        state.isLoading = false;
        const reviewId = action.payload;

        // Remove review from reviews array
        state.reviews = state.reviews.filter(r => r.id !== reviewId);

        // Clear activeReview if it matches
        if (state.activeReview && state.activeReview.id === reviewId) {
          state.activeReview = null;
        }

        // Track deleted review ID for 404 handling
        // Initialize array if undefined (handles Redux Persist rehydration)
        if (!state.deletedReviewIds) {
          state.deletedReviewIds = [];
        }
        if (!state.deletedReviewIds.includes(reviewId)) {
          state.deletedReviewIds.push(reviewId);
          // Keep only last 10 deleted IDs to prevent array growth
          if (state.deletedReviewIds.length > 10) {
            state.deletedReviewIds.shift();
          }
        }

        // Remove on-demand items for this review
        // Initialize object if undefined
        if (!state.onDemandItems) {
          state.onDemandItems = {};
        }
        if (state.onDemandItems[reviewId]) {
          delete state.onDemandItems[reviewId];
        }
      })
      .addCase(deleteReview.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to delete review';
      })
      // Complete Review
      .addCase(completeReview.pending, (state) => {
      })
      .addCase(completeReview.fulfilled, (state, action) => {
        const completedReview = action.payload.review;

        // Update activeReview if it matches
        if (state.activeReview && state.activeReview.id === completedReview.id) {
          state.activeReview.status = completedReview.status;
          state.activeReview.completedAt = completedReview.completedAt;
        }

        // Update the review in the reviews array
        const reviewIndex = state.reviews.findIndex(r => r.id === completedReview.id);
        if (reviewIndex !== -1) {
          state.reviews[reviewIndex].status = completedReview.status;
          state.reviews[reviewIndex].completedAt = completedReview.completedAt;
        }
      })
      .addCase(completeReview.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to complete review';
      })
      // On-demand items
      .addCase(fetchOnDemandItems.pending, (state) => {
        state.isLoadingOnDemand = true;
      })
      .addCase(fetchOnDemandItems.fulfilled, (state, action) => {
        state.isLoadingOnDemand = false;
        // Store on-demand items by review ID
        const reviewId = action.meta.arg;
        // Ensure onDemandItems exists
        if (!state.onDemandItems) {
          state.onDemandItems = {};
        }
        state.onDemandItems[reviewId] = action.payload;
      })
      .addCase(fetchOnDemandItems.rejected, (state, action) => {
        state.isLoadingOnDemand = false;
        state.error = action.error.message || 'Failed to fetch on-demand items';
      })
      .addCase(createOnDemandItem.pending, (state) => {
        state.isLoadingOnDemand = true;
      })
      .addCase(createOnDemandItem.fulfilled, (state, action) => {
        state.isLoadingOnDemand = false;
        const reviewId = action.meta.arg.reviewId;
        // Ensure onDemandItems exists
        if (!state.onDemandItems) {
          state.onDemandItems = {};
        }
        if (!state.onDemandItems[reviewId]) {
          state.onDemandItems[reviewId] = [];
        }
        state.onDemandItems[reviewId].push(action.payload);
      })
      .addCase(createOnDemandItem.rejected, (state, action) => {
        state.isLoadingOnDemand = false;
        state.error = action.error.message || 'Failed to create on-demand item';
      })
      .addCase(updateOnDemandItem.pending, (state) => {
        state.isLoadingOnDemand = true;
        state.error = null; // Clear sticky error
      })
      .addCase(updateOnDemandItem.fulfilled, (state, action) => {
        state.isLoadingOnDemand = false;
        state.error = null; // Clear error on success
        const reviewId = action.meta.arg.reviewId;
        const updatedItem = action.payload;

        // Ensure onDemandItems structure exists
        if (!state.onDemandItems) {
          state.onDemandItems = {};
        }

        // Find and update using normalized ID comparison (handles temp/synced IDs)
        const normalizedUpdatedId = normalizeItemId(updatedItem.id);
        const current = state.onDemandItems[reviewId] || [];
        const index = current.findIndex(
          item => normalizeItemId(item.id) === normalizedUpdatedId
        );

        // Create new array with updated item (explicit immutability for React-Redux)
        const nextItems = index !== -1
          ? current.map((item, i) => i === index ? updatedItem : item)
          : [...current, updatedItem];

        // Replace the entire map entry to ensure new reference
        state.onDemandItems = {
          ...(state.onDemandItems || {}),
          [reviewId]: nextItems,
        };

        // Sync template copy in reviews array (guards against undefined)
        const reviewIndex = state.reviews.findIndex(r => r.id === reviewId);
        if (reviewIndex !== -1 && state.reviews[reviewIndex].template?.categories) {
          const updatedCategories = updateItemInCategories(
            state.reviews[reviewIndex].template.categories,
            updatedItem.id,
            updatedItem
          );
          if (updatedCategories) {
            state.reviews[reviewIndex] = {
              ...state.reviews[reviewIndex],
              template: {
                ...state.reviews[reviewIndex].template,
                categories: updatedCategories,
              },
            };
          }
        }

        // Sync template copy in activeReview (guards against undefined)
        if (state.activeReview && state.activeReview.id === reviewId && state.activeReview.template?.categories) {
          const activeReview = state.activeReview;
          const activeTemplate = activeReview.template;
          if (!activeTemplate?.categories) {
            return;
          }
          const updatedCategories = updateItemInCategories(
            activeTemplate.categories,
            updatedItem.id,
            updatedItem
          );
          if (updatedCategories) {
            state.activeReview = {
              ...activeReview,
              template: {
                ...activeTemplate,
                categories: updatedCategories,
              },
            } as HomeReview;
          }
        }
      })
      .addCase(updateOnDemandItem.rejected, (state, action) => {
        state.isLoadingOnDemand = false;
        state.error = action.error.message || 'Failed to update on-demand item';
      })
      .addCase(deleteOnDemandItem.pending, (state) => {
        state.isLoadingOnDemand = true;
      })
      .addCase(deleteOnDemandItem.fulfilled, (state, action) => {
        state.isLoadingOnDemand = false;
        const { reviewId, itemId } = action.payload;
        // Ensure onDemandItems exists
        if (!state.onDemandItems) {
          state.onDemandItems = {};
        }
        if (state.onDemandItems[reviewId]) {
          state.onDemandItems[reviewId] = state.onDemandItems[reviewId].filter(
            item => item.id !== itemId
          );
        }
      })
      .addCase(deleteOnDemandItem.rejected, (state, action) => {
        state.isLoadingOnDemand = false;
        state.error = action.error.message || 'Failed to delete on-demand item';
      })
      // Update Review Notes
      .addCase(updateReviewNotes.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateReviewNotes.fulfilled, (state, action) => {
        state.isLoading = false;
        const syncStatus = action.payload.syncStatus ?? 'synced';
        // Update the notes in the active review
        if (state.activeReview && state.activeReview.id === action.payload.id) {
          state.activeReview.notes = action.payload.notes;
          state.activeReview.syncStatus = syncStatus;
        }
        // Update the notes in the reviews list
        const reviewIndex = state.reviews.findIndex(r => r.id === action.payload.id);
        if (reviewIndex !== -1) {
          state.reviews[reviewIndex].notes = action.payload.notes;
          state.reviews[reviewIndex].syncStatus = syncStatus;
        }
      })
      .addCase(updateReviewNotes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to update notes';
      })
      // Update Review On Demand Projects
      .addCase(updateReviewOnDemandProjects.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateReviewOnDemandProjects.fulfilled, (state, action) => {
        state.isLoading = false;
        if (!action.payload) {
          return;
        }
        const syncStatus = action.payload.syncStatus ?? 'synced';
        if (state.activeReview && state.activeReview.id === action.payload.id) {
          state.activeReview.onDemandProjects = action.payload.onDemandProjects;
          state.activeReview.syncStatus = syncStatus;
        }
        const reviewIndex = state.reviews.findIndex(r => r.id === action.payload.id);
        if (reviewIndex !== -1) {
          state.reviews[reviewIndex].onDemandProjects = action.payload.onDemandProjects;
          state.reviews[reviewIndex].syncStatus = syncStatus;
        }
      })
      .addCase(updateReviewOnDemandProjects.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to update On Demand Projects';
      })
      .addCase(updateReviewDetails.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateReviewDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        if (!action.payload) return;
        const updatedReviewId = String(action.payload.id);

        const mergeDetails = (review: HomeReview) => {
          review.memberName = action.payload.memberName;
          review.memberEmail = action.payload.memberEmail;
          review.homeManagerName = action.payload.homeManagerName;
          review.homeManagerEmail = action.payload.homeManagerEmail;
          review.syncStatus = action.payload.syncStatus ?? 'synced';
        };

        if (state.activeReview && String(state.activeReview.id) === updatedReviewId) {
          mergeDetails(state.activeReview);
        }
        const reviewIndex = state.reviews.findIndex(
          review => String(review.id) === updatedReviewId
        );
        if (reviewIndex !== -1) {
          mergeDetails(state.reviews[reviewIndex]);
        }
      })
      .addCase(updateReviewDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to update review details';
      })
      // Initialize offline services
      .addCase(initializeOfflineServices.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(initializeOfflineServices.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(initializeOfflineServices.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to initialize offline services';
      })
      // Sync offline data
      .addCase(syncOfflineData.pending, (state) => {
      })
      .addCase(syncOfflineData.fulfilled, (state, action) => {
      })
      .addCase(syncOfflineData.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to sync offline data';
      })
      // Save all drafts
      .addCase(saveAllDrafts.pending, (state) => {
      })
      .addCase(saveAllDrafts.fulfilled, (state) => {
      })
      .addCase(saveAllDrafts.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to save drafts';
      })
      // Clear review state on logout
      .addCase(logout.fulfilled, (state) => {
        state.activeReview = null;
        state.reviews = [];
        state.templates = [];
        state.error = null;
      });

};
